import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Heading, Select } from "@foris/avocado-suite";
import { BookingContext } from "../../context/BookingContext";
import { Types } from "../../context/search.reducer";
import { Types as SavedRequestTypes } from "../../context/savedRequest.reducer";
import ProgramSelect from "../AsyncSelectors/ProgramAsyncSelect";
import { Building, ClassroomType } from "@models/ISchema";
import { Preferences as BookingPreferences } from "../../context/types";
import { ContextEDH } from "@context/ContextEDH";
import css from "./preferences.module.scss";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";

type ClassroomTypeParam = (ClassroomType & { label: string; value: string }) | null;
type BuildingTypeParam = (Building & { label: string; value: string }) | null;

interface PreferencesProps {
  isLoadingClassroomTypes?: boolean;
  onBuildingChange?: (buildingId: string) => void;
}

const Preferences = ({ isLoadingClassroomTypes, onBuildingChange }: PreferencesProps) => {
  const { state, dispatch } = useContext(BookingContext);
  const {
    state: { base },
  } = useContext(ContextEDH);
  const [savedProgram, setSavedProgram] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const { t } = useTranslation();
  const { scenario } = useBookingContextUrl();

  const showProgramSelector = isMounted && base?.bookingsProgramRequired !== "HIDDEN";

  /**
   * If a classroomBooking is been edited, set the building and classroom type
   * as the default values on the selectors.
   */
  useEffect(() => {
    if (state?.search?.currentBooking) {
      const originalBuilding = state?.search?.currentBooking?.classroom?.building;
      const savedBuilding = state?.savedRequest?.bookingPreferences?.building;
      const originalClassroomType = state?.search?.currentBooking?.classroom?.classroomType;
      const savedClassroomType = state?.savedRequest?.bookingPreferences?.classroomType;
      const hasSavedPreferences = !!state?.savedRequest?.bookingPreferences;

      const building = hasSavedPreferences ? savedBuilding : originalBuilding;
      const classroomType = hasSavedPreferences ? savedClassroomType : originalClassroomType;

      const buildingValue = building?.id
        ? { value: building?.id, label: building?.name, ...building }
        : null;
      const classroomTypeValue = classroomType?.id
        ? {
            value: classroomType?.id,
            label: classroomType?.name,
            ...classroomType,
          }
        : null;

      // set building and classroom type
      dispatch({
        type: Types.SetPreferences,
        payload: {
          ...(state?.search?.preferences ?? {}),
          building: buildingValue,
          classroomType: classroomTypeValue,
        } as BookingPreferences,
      });

      setSavedProgram(state?.savedRequest?.bookingPreferences?.program);

      dispatch({
        type: SavedRequestTypes.setSavedBookingPreferences,
        payload: null,
      });
    }

    if (!state?.search?.currentBooking) {
      !!state?.savedRequest?.bookingPreferences?.program?.value &&
        setSavedProgram(state?.savedRequest?.bookingPreferences?.program);
    }

    setIsMounted(true);
  }, []);

  const handleSelectClassroomType = (classroomType: ClassroomTypeParam) => {
    const preferences = {
      ...state?.search?.preferences,
      classroomType: classroomType?.value ? classroomType : null,
    };

    dispatch({ type: Types.SetPreferences, payload: preferences });
  };

  const handleSelectBuilding = (building: BuildingTypeParam) => {
    const preferences = {
      ...state?.search?.preferences,
      classroomType: null,
      building: building?.value ? building : null,
    };

    dispatch({ type: Types.SetPreferences, payload: preferences });
    onBuildingChange?.(building?.value);
  };

  return (
    <section className={css.preferences}>
      <Heading className={css.preferences_title} type="h4">
        {t("booking.booking-form.classroom-preferences.title")}
      </Heading>

      <div className={css.preferences_selectors}>
        <Select
          className={css.selector}
          label={t("booking.booking-form.classroom-preferences.selectors.building.label")}
          placeholder={t(
            "booking.booking-form.classroom-preferences.selectors.building.placeholder",
          )}
          isClearable
          options={state?.search?.buildingOptions}
          value={state?.search?.preferences?.building}
          isDisabled={!state?.search?.buildingOptions}
          onChange={handleSelectBuilding}
          maxMenuHeight={150}
        />

        <Select
          key={`classroomType-building-${state?.search?.preferences?.building?.value}`}
          className={css.selector}
          label={t("booking.booking-form.classroom-preferences.selectors.classroom.label")}
          placeholder={t(
            "booking.booking-form.classroom-preferences.selectors.classroom.placeholder",
          )}
          isClearable
          options={state?.search?.classroomTypesOptions}
          value={state?.search?.preferences?.classroomType}
          isDisabled={!state?.search?.classroomTypesOptions}
          isLoading={isLoadingClassroomTypes}
          onChange={handleSelectClassroomType}
          maxMenuHeight={showProgramSelector ? 150 : 110}
        />

        {showProgramSelector && (
          <ProgramSelect
            className={css.selector}
            escenarioId={scenario}
            isRequired={base?.bookingsProgramRequired === "REQUIRED"}
            initialValue={savedProgram}
            maxMenuHeight={110}
          />
        )}
      </div>
    </section>
  );
};

export default Preferences;
